// Grievances between the leader's state and another state, for the diplomacy ledger.
//
//   Sanction  (either direction, blocs expanded to their members)   → 'sanction' | 'tariff'
//   War       (the two states on opposite sides)                     → 'war'
//   War event naming a strike by one of the two                      → 'strike'
//   ControlEpisode (one holds the other's land, occupied / annexed)  → 'occupation'
//
// Everything that started on or before the date is listed; `to` is set only when it ended by then.
//
// Strikes are read from the curated one-line war developments, so they are only listed when the
// text says who struck: exactly one of the two states is named before a strike verb ("US and
// Israeli strikes killed …" is the US striking Iran when the pair is US-Iran). The target must be
// named after the verb, unless the war has exactly one state on each side (then the other side
// is the target). "CENTCOM struck 80 targets" names neither state, so it is skipped.

import type { ControlEpisode, DateStr, War } from '../core/schema.ts';
import type { Grievance } from './api.ts';
import { nameOf, sanctionsBetween, type Diplo } from './relations.ts';
import { endedBy } from './util.ts';
import { pairWindow, sidesAt } from './wars.ts';

const STRIKE = /\b(air ?strikes?|strikes?|struck|bomb(?:ed|ing|s)?|missiles?|drones?|shell(?:ed|ing)|fired|attack(?:ed|s)?|raids?)\b/i;

const OCCUPATION_VERB: Partial<Record<ControlEpisode['status'], string>> = {
  occupied: 'occupies',
  'annexation-claimed': 'claims by annexation',
};

/** How news copy names the states that appear in war developments, besides Country.name. */
const ALIASES: Record<string, string[]> = {
  USA: ['US', 'U.S.', 'United States', 'American'],
  GBR: ['UK', 'U.K.', 'Britain', 'British'],
  ISR: ['Israeli'],
  IRN: ['Iranian'],
  RUS: ['Russian'],
  UKR: ['Ukrainian'],
  CHN: ['Chinese'],
  TWN: ['Taiwanese'],
  IND: ['Indian'],
  PAK: ['Pakistani'],
  AFG: ['Afghan'],
  SYR: ['Syrian'],
  LBN: ['Lebanese'],
  YEM: ['Yemeni'],
  SAU: ['Saudi'],
  TUR: ['Turkish'],
};

function escape(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Whole-word matcher for a state's names. */
function namesOf(d: Diplo, iso3: string): RegExp {
  const names = [nameOf(d, iso3), ...(ALIASES[iso3] ?? [])];
  return new RegExp(`(?<![\\w.])(?:${names.map(escape).join('|')})(?![\\w])`, 'i');
}

function opposite(w: War, a: string, b: string): boolean {
  return (w.sides.a.includes(a) && w.sides.b.includes(b)) || (w.sides.a.includes(b) && w.sides.b.includes(a));
}

function isState(d: Diplo, id: string): boolean {
  const i = d.actors.byId.get(id);
  return i !== undefined && d.actors.list[i]?.kind === 'country';
}

/** One state on each side on `date`: an unnamed target of a strike is the other side. */
function twoStates(d: Diplo, w: War, date: DateStr): boolean {
  const sides = sidesAt(w, date);
  return sides.a.filter((id) => isState(d, id)).length === 1 && sides.b.filter((id) => isState(d, id)).length === 1;
}

/**
 * A war is one grievance each way. War.sides.a is not "the aggressor" in the curated data
 * (Korean War a = PRK, Heglig crisis a = SDN), so no side is named as the one who acted.
 */
function warGrievances(w: War, self: string, other: string, date: DateStr): Grievance[] {
  // The two states were at war with each other only while both were parties (party_spans).
  const together = pairWindow(w, self, other);
  if (!together || together.from > date) {
    return [];
  }

  const sources = [...new Set([...w.sources, ...w.status.sources])];
  const of = (by: string, target: string): Grievance => ({
    by, target, kind: 'war', title: w.name, from: together.from, to: endedBy(together.to, date), sources,
  });
  return [of(self, other), of(other, self)];
}

function strikesIn(d: Diplo, w: War, self: string, other: string, date: DateStr): Grievance[] {
  const out: Grievance[] = [];
  const mention = { [self]: namesOf(d, self), [other]: namesOf(d, other) };
  const pair = [self, other];

  for (const e of w.events) {
    const verb = STRIKE.exec(e.text);
    if (e.date > date || !verb) {
      continue;
    }
    const before = e.text.slice(0, verb.index);
    const after = e.text.slice(verb.index);
    const strikers = pair.filter((id) => mention[id].test(before));
    if (strikers.length !== 1) {
      continue;
    }
    const by = strikers[0];
    const target = by === self ? other : self;
    if (!mention[target].test(after) && !twoStates(d, w, e.date)) {
      continue;
    }
    out.push({ by, target, kind: 'strike', title: e.text, from: e.date, to: e.date, sources: e.sources });
  }
  return out;
}

function occupationsBetween(d: Diplo, episodes: ControlEpisode[], self: string, other: string, date: DateStr): Grievance[] {
  const out: Grievance[] = [];

  for (const e of episodes) {
    const verb = OCCUPATION_VERB[e.status];
    const held = (e.controller === self && e.territory_of === other) || (e.controller === other && e.territory_of === self);
    if (!verb || !held || e.from > date) {
      continue;
    }
    const title = `${nameOf(d, e.controller)} ${verb} territory of ${nameOf(d, e.territory_of)}`;
    out.push({
      by: e.controller, target: e.territory_of, kind: 'occupation', title,
      from: e.from, to: endedBy(e.to, date), sources: e.sources,
    });
  }
  return out;
}

/** Newest first, like the rest of the ledger. */
export function grievancesBetween(d: Diplo, control: ControlEpisode[], self: string, other: string, date: DateStr): Grievance[] {
  if (self === other) {
    return [];
  }

  const wars = d.wars.filter((w) => w.from <= date && opposite(w, self, other) && pairWindow(w, self, other) !== null);
  const out = [
    ...sanctionsBetween(d, self, other, date),
    ...wars.flatMap((w) => warGrievances(w, self, other, date)),
    ...wars.flatMap((w) => strikesIn(d, w, self, other, date)),
    ...occupationsBetween(d, control, self, other, date),
  ];
  return out.sort((a, b) => (a.from < b.from ? 1 : a.from > b.from ? -1 : 0));
}

